/*
 * What the tract panel reads from the Mineral Ownership Report (GIS mor-owners / mor-leases routes):
 * the report's own listings, so the panel says what the report says. Lease status there follows the
 * lease dates on the day it's asked (HBP, HBU, Leased, Expired, Open, or the agreement's type), unlike
 * the stored Ownership.LeasedStatus, which only changes when a lease is saved.
 */

/* One owner of a tract horizon as the MOR lists it, in the report's order: each additional ownership
 * (an heir, a remainderman, an NPRI, ...) right after its parent. */
export interface IMorOwner {
    ownershipId: number;
    parentOwnershipId?: number | null;
    ownerLookUpId?: number | null;
    ownerName?: string | null;
    /* MI for a standard mineral interest, else the additional type: HEIR, REMAINDERMAN, NPRI, ... */
    category?: string | null;
    decimalInterest?: number | null;
    netAcres?: number | null;
    /* What holds the interest, in the report's words; several agreements are joined with ", ". */
    status?: string | null;
    leaseNumber?: string | null;
    /* The owner's contact note count, each note once (what View notes shows). Null or missing when
     * the row names no owner, or the listing came without counts. */
    noteCount?: number | null;
}

/* One lease on a tract horizon as the MOR's lease section lists it. The Brine MOR has no HBP/HBU,
 * option-exercised, operator, well, unit or API number. */
export interface IMorLease {
    leaseholdId: number;
    leaseId?: number | null;
    leaseNumber?: string | null;
    status?: string | null;
    royalty?: number | null;
    leaseDate?: string | null;
    effectiveDate?: string | null;
    expirationDate?: string | null;
    primaryTerm?: string | null;
    optionTerm?: string | null;
    optionExercised?: boolean | null;
    hbp?: boolean | null;
    hbu?: boolean | null;
    lessee?: string | null;
    lessor?: string | null;
    recordingInfo?: string | null;
    operator?: string | null;
    wellName?: string | null;
    unitName?: string | null;
    apiNumber?: string | null;
    isActiveLease?: boolean | null;
    isHistoricalLease?: boolean | null;
    /* Brine only: the hold's WI owners, who hold the lease (the lessee may be someone else). The
     * report doesn't list them; the route adds them. */
    wiOwners?: string[] | null;
}

/* A MOR listing for one tract, as the page holds it while it loads. */
export interface IMorListing<T> {
    tractId: string;
    status: 'loading' | 'ready' | 'error';
    rows: T[];
}

/* Whether a MOR status says an agreement holds the interest: any part but Open or Expired (and a
 * blank status holds nothing). "Leased, Leased" and "Expired, Leased" hold; "Expired" doesn't. */
export const morHeld = (status: string | null | undefined): boolean =>
    (status || '')
        .split(',')
        .map((part) => part.trim().toLowerCase())
        .some((part) => part !== '' && part !== 'open' && part !== 'expired');

/* A top-level owner and the additional ownerships the MOR lists under it. */
export interface IMorFamily<T extends IMorOwner> {
    head: T;
    children: T[];
}

/* The MOR's rows as families: a row naming a parent that's already listed joins that parent's family
 * (the report lists children right after their parent); any other row heads its own. */
export const morFamilies = <T extends IMorOwner>(rows: T[]): Array<IMorFamily<T>> => {
    const families: Array<IMorFamily<T>> = [];
    const byHead = new Map<number, IMorFamily<T>>();
    rows.forEach((row) => {
        const parent = row.parentOwnershipId ? byHead.get(row.parentOwnershipId) : undefined;
        if (parent) {
            parent.children.push(row);
        } else {
            const family = { head: row, children: [] as T[] };
            families.push(family);
            byHead.set(row.ownershipId, family);
        }
    });
    return families;
};

/* The MOR route for a tract horizon, from a KuduTractID's parts. */
export const morRoute = (
    tract: { clientNumber: string; projectNumber: string; sectionNumber: string; tractNumber: string; horizonNumber: string },
    listing: 'mor-owners' | 'mor-leases',
    brine: boolean
): string =>
    `v1.0/gis/clients/${tract.clientNumber}/projects/${tract.projectNumber}/fields/${tract.sectionNumber}/tracts/${tract.tractNumber}` +
    `/horizons/${tract.horizonNumber}/${listing}${brine ? '?brine=true' : ''}`;

/* The map's tract layers that get a Leaseholds tab: the oil and gas Leasehold layer and a Brine
 * contract layer (the two with a MOR listing). */
export const LEASEHOLD_LAYER_ID = 'tracts-leasehold';
export const isBrineLayerId = (layerId: string | undefined): boolean => !!layerId && /^tracts-contract-.*brine/i.test(layerId);



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/morData.ts